import React, { useState, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import useAPI from '../../../../api';
import PropTypes from 'prop-types';
import {
  Button,
  Paper,
  Typography,
  IconButton,
  ButtonGroup,
  ListItemIcon,
  MenuItem,
  ListItemText,
  Tooltip,
} from '@mui/material';
import ChangeCircleOutlinedIcon from '@mui/icons-material/ChangeCircleOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import PersonOffRoundedIcon from '@mui/icons-material/PersonOffRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  activityTypesAtom,
  openSnackbarAdviseAtom,
  selectedBranchAtom,
  userBranchesAtom,
  userDataAtom,
  activityOutcomesAtom,
} from '../../../../Atoms';
import {
  ColumnFlexBox,
  FullSizeCenteredFlexBox,
  CenteredFlexBox,
} from '../../../../Components/FlexBox';
import { createNewDate, culture, options } from '../../../../Helpers/dates';
import ResponseToggleButton from './ResponseToggleButton';
import Information from '../../../PracticesPage/Components/Information';
import ContactsManagerWindow from '../../Shared/ContactsManagerWindow';
import CallKeyPad from './CallKeyPad';
import CaptionValue from '../../../../Components/CaptionValue/CaptionValue';
import capitalize from '../../../../Helpers/StringsFormatting/capitalize';
import CustomMenu from '../../../../Components/CustomMenu';
import {
  ACTIVITY_TYPES_DIALOGS,
  ACTIVITY_TYPES_ICONS,
} from '../../../../Utils/constants/activityTypes';
import ConfirmDialog from '../../../../Components/Dialog/ConfirmDialog';
import { checkFeature } from '../../../../Router/routes';
import EventDetailsDialog from '../../../AppointmentsPage/Components/EventDetailsDialog';

const CallBox = function CallBox({
  contactExpired,
  campaignId,
  contactId,
  reloadHistory,
  callId,
  callBoxState,
  selectedPhoneNumber,
  activityParentId,
  handleStartCall,
  contactPhones,
  handleChangeSelectedPhone,
  handleStartRecall,
  lastContactDate,
  handleAddNewPhoneNumber,
  handleDisablePhoneNumber,
  answered,
  handleChangeAnswered,
  handleChangeContactStatus,
  phoneTypes,
  isLead,
  isOperator,
  contactName,
  removedOn,
  resultBoxState,
  disableAnsweredButtons,
  appointmentToManage,
}) {
  const intl = useIntl();
  const API = useAPI();
  const userData = useAtomValue(userDataAtom);
  const activityTypes = useAtomValue(activityTypesAtom);
  const openSnackbar = useSetAtom(openSnackbarAdviseAtom);
  const activityOutcomes = useAtomValue(activityOutcomesAtom);
  const [phonesMenu, setPhonesMenu] = useState(null);
  const [activityDialog, setActivityDialog] = useState(null);
  const [activityMenu, setActivityMenu] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [openAppointmentCompleteDialog, setOpenAppointmentCompleteDialog] =
    useState(false);
  const [appointmentOutcomes, setAppointmentOutcomes] = useState([]);
  const [editAppointment, setEditAppointment] = useState(null);

  const handleOpenAppointmentCompleteDialog = () => {
    setOpenAppointmentCompleteDialog(true);
  };
  const handleCloseAppointmentCompleteDialog = () => {
    setOpenAppointmentCompleteDialog(false);
    if (editAppointment) setEditAppointment(null);
  };

  const handleOpenPhonesMenu = (e) => {
    setPhonesMenu(e.currentTarget);
  };
  const handleClosePhonesMenu = () => {
    setPhonesMenu(null);
  };

  const handleOpenActivityDialog = (activityTypeName) => {
    setActivityDialog(activityTypeName);
  };
  const handleCloseActivityDialog = () => {
    setActivityDialog(null);
    if (editAppointment) setEditAppointment(null);
  };

  useEffect(() => {
    if (editAppointment) {
      setActivityDialog('APPOINTMENT');
    }
  }, [editAppointment]);

  const selectedBranch = useAtomValue(selectedBranchAtom);
  const userBranches = useAtomValue(userBranchesAtom);

  const handleClick = () => {
    if (removedOn) setConfirmDialog(true);
    else {
      if (activityParentId) handleStartRecall();
      else handleStartCall();
    }
  };

  const handleOpenActivityMenu = (event) => {
    setActivityMenu(event.currentTarget);
  };
  const handleCloseActivityMenu = () => {
    setActivityMenu(null);
  };

  useEffect(() => {
    if (Object.keys(activityOutcomes || {}).length > 0) {
      setAppointmentOutcomes(activityOutcomes?.APPOINTMENT);
    }
  }, [activityOutcomes]);

  const handleRegisterInboundCall = (data) => {
    API.activity(
      {
        activityTypeId: activityTypes.find((type) => type.name === 'CALL').id,
        branchId: selectedBranch.id,
        entityId: contactId,
        notes: data.notes,
        campaignId,
        outcome: data.outcome,
        nextActivityData:
          Object.keys(data.nextActivityData).length > 0
            ? JSON.stringify(data.nextActivityData)
            : null,
      },
      {
        outbound: false,
        phoneNumber: selectedPhoneNumber,
      },
      {},
      () => {
        reloadHistory();
        handleCloseActivityDialog();
        openSnackbar({
          message: intl.messages.INBOUND_CALL_CREATED,
          severity: 'success',
        });
      },
      () => {
        openSnackbar({
          message: intl.messages.INBOUND_CALL_ERROR,
          severity: 'error',
        });
      }
    );
  };

  const handleAddNewAppointment = (data) => {
    if (data && selectedBranch) {
      const { date, time, desc, assignee, remindBefore } = data;
      date.setHours(time.getHours());
      date.setMinutes(time.getMinutes());

      API.activity(
        {
          activityTypeId: activityTypes.find(
            (type) => type.name === 'APPOINTMENT'
          ).id,
          branchId: selectedBranch.id,
          entityId: contactId,
          notes: desc,
          campaignId,
        },
        {
          scheduledOn: date.toISOString(),
          assignee: assignee === 'ALL' ? null : assignee,
          remindBefore: remindBefore,
        },
        {},
        () => {
          reloadHistory();
          handleCloseActivityDialog();
          openSnackbar({
            message: intl.messages.APPOINTMENT_CREATED,
            severity: 'success',
          });
        },
        () => {
          openSnackbar({
            message: intl.messages.CREATE_APP_ERROR,
            severity: 'error',
          });
        }
      );
    }
  };

  const handleEditAppointment = (data) => {
    if (data && selectedBranch) {
      const { date, time } = data;
      date.setHours(time.getHours());
      date.setMinutes(time.getMinutes());

      API.updateAppointment(
        appointmentToManage?.appointmentId,
        {
          entityId: data.customer.id,
          assignee: data.assignee === 'ALL' ? null : data.assignee,
          notes: data.desc,
          scheduledOn: date.toISOString(),
          remindBefore: data.remindBefore || null,
        },
        () => {
          handleCloseAppointmentCompleteDialog();
          handleCloseActivityDialog();
          openSnackbar({
            message: intl.messages.APPOINTMENT_EDITED,
            severity: 'success',
          });
        },
        () => {
          openSnackbar({
            message: intl.messages.EDIT_APP_ERROR,
            severity: 'error',
          });
        }
      );
    }
  };

  const handleCreateRecall = (data) => {
    API.activity(
      {
        activityTypeId: activityTypes.find((type) => type.name === 'CALL').id,
        branchId: selectedBranch.id,
        entityId: contactId,
        notes: data.notes,
        campaignId,
      },
      {
        outbound: true,
        phoneNumber: selectedPhoneNumber,
        scheduledOn: data.scheduledOn,
        assignee: data.assignee === 'ALL' ? null : data.assignee,
      },
      {},
      () => {
        reloadHistory();
        handleCloseActivityDialog();
        openSnackbar({
          message: intl.messages.INBOUND_CALL_CREATED,
          severity: 'success',
        });
      },
      () => {
        openSnackbar({
          message: intl.messages.INBOUND_CALL_ERROR,
          severity: 'error',
        });
      }
    );
  };

  const handleCreateManualActivity = (data) => {
    API.activity(
      {
        activityTypeId: activityTypes.find((type) => type.name === 'MANUAL').id,
        branchId: selectedBranch.id,
        entityId: contactId,
        notes: data.notes,
        campaignId,
      },
      {
        campaignAdvancement: data.campaignAdvancement,
      },
      {},
      () => {
        reloadHistory();
        handleCloseActivityDialog();
        openSnackbar({
          message: intl.messages.MANUAL_ACTIVITY_CREATED,
          severity: 'success',
        });
      },
      () => {
        openSnackbar({
          message: intl.messages.CREATE_MANUAL_ACTIVITY_ERROR,
          severity: 'error',
        });
      }
    );
  };

  const handleEndAppointment = (
    { entityId, outcome, notes, nextActivityData },
    onSuccess
  ) => {
    API.endAppointment(
      appointmentToManage?.appointmentId,
      {
        entityId,
        branchId: selectedBranch.id,
        outcome,
        notes,
        activityTypeId: activityTypes.find(
          (type) => type.name === 'APPOINTMENT'
        )?.id,
        nextActivityData,
      },
      () => {
        openSnackbar({
          message: intl.messages.APPOINTMENT_COMPLETED,
          severity: 'info',
        });
        if (onSuccess) {
          onSuccess();
        }
        reloadHistory();
        handleCloseAppointmentCompleteDialog();
      },
      () => {
        openSnackbar({
          message: intl.messages.ERROR,
          severity: 'error',
        });
      }
    );
  };
  const ActivityDialog = useMemo(
    () => ACTIVITY_TYPES_DIALOGS[activityDialog],
    [activityDialog]
  );

  return (
    <>
      <EventDetailsDialog
        open={openAppointmentCompleteDialog}
        onClose={handleCloseAppointmentCompleteDialog}
        outcomes={appointmentOutcomes}
        event={appointmentToManage}
        setEdit={setEditAppointment}
        handleEndAppointment={handleEndAppointment}
      />
      <ConfirmDialog
        open={confirmDialog}
        onCancel={() => setConfirmDialog(false)}
        onSubmit={() => {
          if (activityParentId) handleStartRecall();
          else handleStartCall();

          setConfirmDialog(false);
        }}
        title="CONFIRM_CALL_CONTACT"
        question="REMOVED_CONTACT_CALL_CONFIRM"
        fontSize="1.5rem"
      />
      {activityDialog && (
        <ActivityDialog
          open={Boolean(activityDialog)}
          onClose={handleCloseActivityDialog}
          {...(activityDialog === 'APPOINTMENT'
            ? {
                title: 'NEW_APPOINTMENT',
                selectedBranch: selectedBranch,
                onSubmit: editAppointment
                  ? handleEditAppointment
                  : handleAddNewAppointment,
                isOperator: isOperator,
                hideCustomerField: true,
                defaultData: appointmentToManage,
              }
            : {})}
          {...(activityDialog === 'MANUAL'
            ? { onSubmit: handleCreateManualActivity }
            : {})}
          {...(activityDialog === 'CALL'
            ? {
                callId: callId,
                isOperator: isOperator,
                phoneNumber: selectedPhoneNumber,
                campaignId: campaignId,
                onSubmit: handleRegisterInboundCall,
              }
            : {})}
          {...(activityDialog === 'RECALL'
            ? {
                callId: callId,
                isOperator: isOperator,
                phoneNumber: selectedPhoneNumber,
                onSubmit: handleCreateRecall,
              }
            : {})}
        />
      )}
      <ContactsManagerWindow
        isPhone
        title={`${intl.messages.PHONE_NUMBERS}:`}
        anchorEl={phonesMenu}
        open={Boolean(phonesMenu)}
        close={handleClosePhonesMenu}
        selectedItem={selectedPhoneNumber}
        handleChangeContactStatus={handleChangeContactStatus}
        onChangeSelection={(number) =>
          selectedPhoneNumber !== number && handleChangeSelectedPhone(number)
        }
        valueTypesArray={phoneTypes}
        disableContact={handleDisablePhoneNumber}
        addNewContact={handleAddNewPhoneNumber}
        sourceArray={contactPhones?.map(
          ({ phoneNumber, phoneType, active, phoneTypeId }) => {
            return {
              typeId: phoneTypeId,
              value: phoneNumber,
              type: phoneType,
              active,
            };
          }
        )}
      />
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '4px solid',
          borderColor: 'primary.main',
          backgroundColor: 'primary.main',
        }}
      >
        <ColumnFlexBox
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: '0px 0px 18px 18px',
          }}
          padding="16px"
          alignItems="flex-end"
          gap="8px"
        >
          <CaptionValue
            width="100%"
            caption={intl.messages.CONTACT_NAME}
            value={capitalize(contactName)}
          />
          <Information
            styles={{ root: { width: '100%', gap: '0px' } }}
            caption={intl.messages.PHONE}
            value={
              <FullSizeCenteredFlexBox gap="8px">
                <Typography
                  key={selectedPhoneNumber}
                  variant="h3"
                  sx={{ fontSize: '30px' }}
                >
                  {userBranches.length > 1 &&
                  selectedBranch &&
                  selectedPhoneNumber ? (
                    <a
                      style={{
                        color: 'inherit',
                      }}
                      href={`tel:${selectedBranch.prefix3CX}${selectedPhoneNumber.replace('+39', '')}`}
                    >
                      {selectedPhoneNumber}
                    </a>
                  ) : (
                    selectedPhoneNumber || intl.messages.UNAVAILABLE
                  )}
                </Typography>
                {!callBoxState && !isLead && (
                  <IconButton onClick={handleOpenPhonesMenu}>
                    <ChangeCircleOutlinedIcon />
                  </IconButton>
                )}
              </FullSizeCenteredFlexBox>
            }
          />

          {callBoxState !== null ? (
            <>
              {userData?.voIPDevice ? ( //if a device is linked to current user it will activate the integration to make real calls by App
                <CallKeyPad callId={callId} />
              ) : (
                <div />
              )}
            </>
          ) : (
            <>
              <CustomMenu
                open={Boolean(activityMenu)}
                anchor={activityMenu}
                close={handleCloseActivityMenu}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {!checkFeature('HIDE_REGISTER_INBOUNDCALL') && (
                  <MenuItem
                    id="inboundcall-menu-item"
                    onClick={() => {
                      handleOpenActivityDialog('CALL');
                      handleCloseActivityMenu();
                    }}
                    sx={{ minWidth: '200px' }}
                  >
                    <ListItemIcon>
                      <ACTIVITY_TYPES_ICONS.CALL />
                    </ListItemIcon>
                    <ListItemText>{intl.messages.INBOUND_CALL}</ListItemText>
                  </MenuItem>
                )}
                <MenuItem
                  id="appointment-menu-item"
                  onClick={() => {
                    handleOpenActivityDialog('APPOINTMENT');
                    handleCloseActivityMenu();
                  }}
                  sx={{ minWidth: '200px' }}
                >
                  <ListItemIcon>
                    <ACTIVITY_TYPES_ICONS.APPOINTMENT />
                  </ListItemIcon>
                  <ListItemText>
                    {intl.messages.APPOINTMENT_SINGLE}
                  </ListItemText>
                </MenuItem>
                <MenuItem
                  id="recall-menu-item"
                  onClick={() => {
                    handleOpenActivityDialog('RECALL');
                    handleCloseActivityMenu();
                  }}
                  sx={{ minWidth: '200px' }}
                >
                  <ListItemIcon>
                    <ACTIVITY_TYPES_ICONS.CALL />
                  </ListItemIcon>
                  <ListItemText>{intl.messages.RECALL}</ListItemText>
                </MenuItem>
                <MenuItem
                  id="manual-menu-item"
                  onClick={() => {
                    handleOpenActivityDialog('MANUAL');
                    handleCloseActivityMenu();
                  }}
                  sx={{ minWidth: '200px' }}
                >
                  <ListItemIcon>
                    <ACTIVITY_TYPES_ICONS.MANUAL />
                  </ListItemIcon>
                  <ListItemText>{intl.messages.MANUAL}</ListItemText>
                </MenuItem>
              </CustomMenu>
              <FullSizeCenteredFlexBox
                justifyContent={
                  removedOn || appointmentToManage
                    ? 'space-between'
                    : 'flex-end'
                }
                gap={appointmentToManage && '16px'}
                mt="24px"
              >
                {appointmentToManage && (
                  <Button
                    id="complete-appointment-btn"
                    size="large"
                    sx={{ height: '56px', width: '50%' }}
                    onClick={handleOpenAppointmentCompleteDialog}
                    startIcon={<EventAvailableRoundedIcon />}
                  >
                    {intl.messages.COMPLETE_APPOINTMENT}
                  </Button>
                )}
                {removedOn && (
                  <CenteredFlexBox flexGrow={1} justifyContent="center">
                    <Tooltip
                      placement="top"
                      slotProps={{
                        tooltip: { sx: { fontSize: '1rem', padding: '8px' } },
                      }}
                      title={`${intl.messages.NEVER_CALL_AGAIN_AGAIN}${new Date(removedOn)?.toLocaleDateString(culture, options)}`}
                      arrow
                    >
                      <PersonOffRoundedIcon
                        sx={{ color: 'error.main', fontSize: '2.5rem' }}
                      />
                    </Tooltip>
                  </CenteredFlexBox>
                )}
                <ButtonGroup
                  variant="contained"
                  sx={{
                    borderRadius: '50px',
                    width: appointmentToManage ? '50%' : 'fit-content',
                  }}
                >
                  <Button
                    id="start-call-btn"
                    size="large"
                    sx={{ height: '56px' }}
                    startIcon={<LocalPhoneOutlinedIcon />}
                    onClick={handleClick}
                    disabled={contactExpired || !selectedPhoneNumber}
                  >
                    {activityParentId
                      ? intl.messages.START_RECALL
                      : intl.messages.START_CALL}
                  </Button>
                  <Button onClick={handleOpenActivityMenu} size="small">
                    <KeyboardArrowDownRoundedIcon />
                  </Button>
                </ButtonGroup>
              </FullSizeCenteredFlexBox>
            </>
          )}
        </ColumnFlexBox>
        <CenteredFlexBox
          sx={{
            color: '#fff',
          }}
          height="76px"
          flexGrow={0}
          padding="8px 4px 4px 4px"
          gap="8px"
          justifyContent="space-between"
        >
          {callBoxState !== null && resultBoxState ? (
            <ResponseToggleButton
              disableAnsweredButtons={disableAnsweredButtons}
              answered={answered}
              handleChangeAnswered={handleChangeAnswered}
            />
          ) : (
            <Information
              styles={{
                root: { paddingLeft: '24px' },
                captionStyle: { color: '#fff' },
              }}
              caption={intl.messages.LAST_CONTACT}
              value={
                lastContactDate
                  ? `${createNewDate(lastContactDate).toLocaleDateString(
                      culture,
                      options
                    )}`
                  : undefined
              }
            />
          )}
        </CenteredFlexBox>
      </Paper>
    </>
  );
};

CallBox.propTypes = {
  callId: PropTypes.string,
  inCall: PropTypes.bool,
  contactPhones: PropTypes.array,
  handleChangeSelectedPhone: PropTypes.func,
  selectedPhoneNumber: PropTypes.string.isRequired,
  activityParentId: PropTypes.string,
  handleStartCall: PropTypes.func,
  lastContactDate: PropTypes.object,
  handleStartRecall: PropTypes.func,
  handleAddNewPhoneNumber: PropTypes.func,
  handleDisablePhoneNumber: PropTypes.func,
  answered: PropTypes.bool,
  handleChangeAnswered: PropTypes.func,
  callBoxState: PropTypes.bool,
};
export default CallBox;
